// ============================================================================
// Booking Constants — Single source of truth for all booking-related values
// ============================================================================
//
// Naming convention:
//   *_VALUES  = plain string tuples for Zod z.enum()
//   *_OPTIONS = { value, label } arrays for UI dropdowns
//   Types     = derived from VALUES via typeof

// --- Equipment Types ---

export const EQUIPMENT_TYPE_VALUES = ["single", "double", "canoe"] as const
export type EquipmentType = (typeof EQUIPMENT_TYPE_VALUES)[number]

export const EQUIPMENT_TYPE_OPTIONS = [
  { value: "single", label: "Single Kayak" },
  { value: "double", label: "Double Kayak" },
  { value: "canoe", label: "Canoe" },
] as const satisfies ReadonlyArray<{ value: EquipmentType; label: string }>

export const EQUIPMENT_TYPE_LABELS: Record<EquipmentType, string> = {
  single: "Single",
  double: "Double",
  canoe: "Canoe",
}

export const EQUIPMENT_TYPE_BADGE_COLORS: Record<EquipmentType, string> = {
  single: "bg-blue-100 text-blue-800 border-blue-200",
  double: "bg-purple-100 text-purple-800 border-purple-200",
  canoe: "bg-green-100 text-green-800 border-green-200",
}

/** Equipment types where size is always "standard" (no size choice). */
export const STANDARD_ONLY_EQUIPMENT = new Set<EquipmentType>(["canoe"])

// --- Equipment Sizes ---

export const EQUIPMENT_SIZE_VALUES = ["standard", "xl", "personal"] as const
export type EquipmentSize = (typeof EQUIPMENT_SIZE_VALUES)[number]

export const EQUIPMENT_SIZE_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "xl", label: "XL" },
  { value: "personal", label: "Personal" },
] as const satisfies ReadonlyArray<{ value: EquipmentSize; label: string }>

export const EQUIPMENT_SIZE_LABELS: Record<EquipmentSize, string> = {
  standard: "Standard",
  xl: "XL",
  personal: "Personal",
}

/** Short labels for compact UI (e.g. calendar cards). */
export const EQUIPMENT_SIZE_SHORT_LABELS: Record<EquipmentSize, string> = {
  standard: "Std",
  xl: "XL",
  personal: "Personal",
}

// --- Guest Types ---

export const GUEST_TYPE_VALUES = ["lodge", "camping", "mixed"] as const
export type GuestType = (typeof GUEST_TYPE_VALUES)[number]

export const GUEST_TYPE_OPTIONS = [
  { value: "lodge", label: "Lodge" },
  { value: "camping", label: "Camper" },
  { value: "mixed", label: "Mixed" },
] as const satisfies ReadonlyArray<{ value: GuestType; label: string }>

export const GUEST_TYPE_LABELS: Record<GuestType, string> = {
  lodge: "Lodge",
  camping: "Camper",
  mixed: "Mixed",
}

export const GUEST_TYPE_BADGE_COLORS: Record<GuestType, string> = {
  lodge: "bg-emerald-50 text-emerald-700 border-emerald-200",
  camping: "bg-orange-50 text-orange-700 border-orange-200",
  mixed: "bg-indigo-50 text-indigo-700 border-indigo-200",
}

// --- Kayak Inventory ---

export type KayakInventory = Record<
  EquipmentType,
  Partial<Record<EquipmentSize, number>>
>

// --- Scheduled Times ---

export const SCHEDULED_TIME_VALUES = [
  "09:15",
  "10:15",
  "11:15",
  "12:15",
  "15:15",
  "16:00",
] as const
export type ScheduledTime = (typeof SCHEDULED_TIME_VALUES)[number]

export const SCHEDULED_TIME_OPTIONS = [
  { value: "09:15", label: "9:15 AM" },
  { value: "10:15", label: "10:15 AM" },
  { value: "11:15", label: "11:15 AM" },
  { value: "12:15", label: "12:15 PM" },
  { value: "15:15", label: "3:15 PM" },
  { value: "16:00", label: "4:00 PM" },
] as const satisfies ReadonlyArray<{ value: ScheduledTime; label: string }>

// --- Trip Types ---

export const TRIP_TYPE_VALUES = ["arrival", "departure", "custom"] as const
export type TripType = (typeof TRIP_TYPE_VALUES)[number]

export const TRIP_TYPE_OPTIONS = [
  { value: "arrival", label: "Arrival Transfer" },
  { value: "departure", label: "Departure Transfer" },
  { value: "custom", label: "Custom Trip" },
] as const satisfies ReadonlyArray<{ value: TripType; label: string }>

// --- Dietary Types ---

export const DIETARY_TYPE_VALUES = [
  "none",
  "vegetarian",
  "vegan",
  "gluten-free",
  "dairy-free",
  "halal",
  "kosher",
  "other",
] as const
export type DietaryType = (typeof DIETARY_TYPE_VALUES)[number]

export const DIETARY_TYPE_OPTIONS = [
  { value: "none", label: "No Restrictions" },
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "gluten-free", label: "Gluten-Free" },
  { value: "dairy-free", label: "Dairy-Free" },
  { value: "halal", label: "Halal" },
  { value: "kosher", label: "Kosher" },
  { value: "other", label: "Other" },
] as const satisfies ReadonlyArray<{ value: DietaryType; label: string }>

// --- Allergy Severities ---

export const ALLERGY_SEVERITY_VALUES = ["moderate", "severe"] as const
export type AllergySeverity = (typeof ALLERGY_SEVERITY_VALUES)[number]

export const ALLERGY_SEVERITY_OPTIONS = [
  { value: "moderate", label: "Moderate" },
  { value: "severe", label: "Severe" },
] as const satisfies ReadonlyArray<{ value: AllergySeverity; label: string }>
